<script lang="ts">
  import { api, ApiError } from '../../lib/api';
  import { persistSession, userScore } from '../../stores/auth';
  import { firebaseAuth } from '../../lib/firebase';
  import {
    signInWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider,
  } from 'firebase/auth';
  import { isAdmin } from '../../stores/tournaments';

  let email = $state('');
  let password = $state('');
  let loading = $state(false);
  let errorMsg = $state('');

  // Flow Google: apos signInWithPopup, se /users/me da 404 (primeiro
  // login), pede só o nickname inline. Cellphone fica vazio nesse caminho.
  let needsNickname = $state(false);
  let pendingNickname = $state('');

  type Me = { id: string; nickname: string; avatar?: string; balance_cents?: number; is_admin?: boolean; score?: number };

  // Mapeia codigos de erro do Firebase Auth pra mensagens em PT-BR.
  // Codigos completos: https://firebase.google.com/docs/reference/js/auth#autherrorcodes
  function friendlyFirebaseError(code: string): string {
    switch (code) {
      case 'auth/invalid-email':
        return 'Email invalido.';
      case 'auth/user-disabled':
        return 'Conta desabilitada.';
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Email ou senha incorretos.';
      case 'auth/too-many-requests':
        return 'Muitas tentativas. Tenta de novo em alguns minutos.';
      case 'auth/network-request-failed':
        return 'Sem conexao. Verifica sua rede.';
      case 'auth/popup-closed-by-user':
        return 'Janela do Google fechada antes de concluir.';
      case 'auth/popup-blocked':
        return 'Popup bloqueado pelo navegador. Libera popup pro site.';
      case 'auth/cancelled-popup-request':
        return ''; // benigno — popup duplicado, ignorar silenciosamente
      case 'auth/account-exists-with-different-credential':
        return 'Esse email ja foi cadastrado com outro metodo (use email+senha).';
      case 'auth/unauthorized-domain':
        return 'Login com Google indisponivel neste dominio. Use email e senha.';
      case 'auth/operation-not-allowed':
        return 'Login com Google nao esta habilitado. Use email e senha.';
      default:
        return `Erro: ${code}`;
    }
  }

  // Apos qualquer login (email/senha ou Google), busca o profile e
  // hidrata os stores. Retorna true se foi tudo certo, false se nao
  // tem profile ainda (caller decide proxima acao).
  async function loadProfileOrFail(): Promise<boolean> {
    try {
      const me = await api.get<Me>('/users/me');
      persistSession('', me.id, me.nickname, me.avatar);
      isAdmin.set(!!me.is_admin);
      userScore.set(me.score ?? 1500);
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        return false;
      }
      throw err;
    }
  }

  // Tira espacos e caracteres meio chatos do nome do Google (acentos
  // preservados; emoji nao). Limita a 32 chars (mesmo limite do backend).
  function nicknameFromGoogle(displayName: string | null): string {
    if (!displayName) return '';
    return displayName.trim().slice(0, 32);
  }

  async function handleGoogleSignIn() {
    if (loading) return;
    errorMsg = '';
    loading = true;
    try {
      const provider = new GoogleAuthProvider();
      const cred = await signInWithPopup(firebaseAuth, provider);
      const ok = await loadProfileOrFail();
      if (!ok) {
        // Primeiro login (ou profile ausente) — pede so o nickname.
        pendingNickname = nicknameFromGoogle(cred.user.displayName);
        needsNickname = true;
      }
    } catch (err: unknown) {
      const fbErr = err as { code?: string };
      if (fbErr && typeof fbErr.code === 'string' && fbErr.code.startsWith('auth/')) {
        const msg = friendlyFirebaseError(fbErr.code);
        if (msg) errorMsg = msg;
      } else {
        errorMsg = String(err);
      }
    } finally {
      loading = false;
    }
  }

  async function finishGoogleSignup(e: Event) {
    e.preventDefault();
    if (loading) return;
    errorMsg = '';
    loading = true;
    try {
      const me = await api.post<Me>('/users/profile', {
        nickname: pendingNickname.trim(),
        cellphone: '',
      });
      persistSession('', me.id, me.nickname, me.avatar);
      isAdmin.set(!!me.is_admin);
      userScore.set(me.score ?? 1500);
      needsNickname = false;
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        errorMsg = err.status === 409
          ? 'Esse nickname ja esta em uso, escolhe outro.'
          : `Erro ${err.status}: ${err.message}`;
      } else {
        errorMsg = String(err);
      }
    } finally {
      loading = false;
    }
  }

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (loading) return;
    errorMsg = '';
    loading = true;
    try {
      // Login via Firebase Auth. O onIdTokenChanged em stores/auth.ts
      // ja captura o ID token e atualiza o store. So precisamos pegar
      // o profile via /users/me (com o token novo, ja injetado pelo
      // api wrapper) pra preencher userId/userName/avatar.
      await signInWithEmailAndPassword(firebaseAuth, email, password);
      const ok = await loadProfileOrFail();
      if (!ok) {
        // Conta existe no Firebase Auth mas o profile nunca foi criado no
        // Firestore (ex: registro anterior caiu no meio do POST /users/profile).
        // Antes disso mostrava um erro sem saida ("faz cadastro completo",
        // que falha com email-already-in-use) — agora reaproveita o mesmo
        // fluxo de completar nickname que o login com Google ja tinha.
        pendingNickname = '';
        needsNickname = true;
      }
    } catch (err: unknown) {
      const fbErr = err as { code?: string; message?: string };
      if (fbErr && typeof fbErr.code === 'string' && fbErr.code.startsWith('auth/')) {
        errorMsg = friendlyFirebaseError(fbErr.code);
      } else if (err instanceof ApiError) {
        errorMsg = `Erro ${err.status}: ${err.message}`;
      } else {
        errorMsg = String(err);
      }
    } finally {
      loading = false;
    }
  }
</script>

{#if needsNickname}
  <!-- Primeiro login, ou profile ausente no servidor — pede so o nickname. -->
  <form onsubmit={finishGoogleSignup} class="google-finish">
    <p class="muted">Quase la! Escolhe um nickname pra terminar o cadastro (ou deixa em branco que a gente escolhe um).</p>
    <label>
      <span>nickname (opcional)</span>
      <input
        type="text"
        bind:value={pendingNickname}
        autocomplete="nickname"
        disabled={loading}
        maxlength="32"
        autofocus
      />
    </label>

    {#if errorMsg}
      <div class="error" role="alert">{errorMsg}</div>
    {/if}

    <button type="submit" disabled={loading} class="primary">
      {loading ? 'criando...' : 'criar conta'}
    </button>
  </form>
{:else}
  <form onsubmit={handleSubmit}>
    <label>
      <span>email</span>
      <input
        type="email"
        bind:value={email}
        autocomplete="email"
        required
        disabled={loading}
      />
    </label>

    <label>
      <span>senha</span>
      <input
        type="password"
        bind:value={password}
        autocomplete="current-password"
        required
        disabled={loading}
      />
    </label>

    {#if errorMsg}
      <div class="error" role="alert">{errorMsg}</div>
    {/if}

    <button type="submit" disabled={loading || !email || !password} class="primary">
      {loading ? 'entrando...' : 'entrar'}
    </button>
  </form>

  <div class="divider"><span>ou</span></div>

  <button
    type="button"
    class="google-btn"
    onclick={handleGoogleSignIn}
    disabled={loading}
  >
    <svg class="g-icon" viewBox="0 0 18 18" aria-hidden="true">
      <path d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.84 2.09-1.79 2.73v2.27h2.9c1.7-1.56 2.69-3.87 2.69-6.64z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.9-2.27c-.81.54-1.85.86-3.06.86-2.35 0-4.34-1.59-5.05-3.73H.94v2.34C2.42 15.98 5.48 18 9 18z" fill="#34A853"/>
      <path d="M3.95 10.71c-.18-.54-.28-1.12-.28-1.71s.1-1.17.28-1.71V4.96H.94C.34 6.17 0 7.55 0 9c0 1.45.34 2.83.94 4.04l3.01-2.33z" fill="#FBBC05"/>
      <path d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.47.89 11.43 0 9 0 5.48 0 2.42 2.02.94 4.96l3.01 2.34C4.66 5.17 6.65 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
    {loading ? 'aguarde...' : 'Entrar com Google'}
  </button>
{/if}

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  label span {
    font-size: 11px;
    color: #0D47A1;
    font-weight: 800;
    letter-spacing: 1px;
    text-transform: uppercase;
  }
  /* Inputs legiveis no card creme (tema cartoon) */
  form input {
    width: 100%;
    box-sizing: border-box;
    padding: 11px 12px;
    font-size: 15px;
    color: #0D47A1;
    background: #fff;
    border: 2px solid #90caf9;
    border-radius: 8px;
    outline: none;
    transition: border-color 0.12s, box-shadow 0.12s;
  }
  form input::placeholder { color: #90a4ae; }
  form input:focus {
    border-color: #1565C0;
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.18);
  }
  form input:disabled { opacity: 0.6; }
  .error {
    color: #c62828;
    background: rgba(198, 40, 40, 0.08);
    border: 1.5px solid rgba(198, 40, 40, 0.35);
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 600;
  }
  .primary {
    background: linear-gradient(180deg, #42a5f5, #1976d2 55%, #1565c0);
    color: #fff;
    border: 2px solid #0D47A1;
    border-radius: 10px;
    font-weight: 900;
    letter-spacing: 1px;
    text-transform: uppercase;
    padding: 12px;
    margin-top: 4px;
    cursor: pointer;
    box-shadow: 0 3px 0 #0d3a82, inset 0 1px 0 rgba(255, 255, 255, 0.25);
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
    transition: transform 0.08s, box-shadow 0.08s, filter 0.12s;
  }
  .primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 0 #0d3a82, inset 0 1px 0 rgba(255,255,255,0.25); }
  .primary:active:not(:disabled) { transform: translateY(2px); box-shadow: 0 1px 0 #0d3a82; }
  .primary:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Divider "ou" entre login email/senha e Google */
  .divider {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 14px 0 10px;
    color: #1976D2;
    font-weight: 700;
    font-size: 10px;
    letter-spacing: 2px;
    text-transform: uppercase;
  }
  .divider::before,
  .divider::after {
    content: '';
    flex: 1;
    height: 2px;
    background: rgba(21, 101, 192, 0.2);
  }
  .divider span { padding: 0 4px; }

  /* Botao Google — branco com logo colorido, padrao Google brand */
  .google-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 12px;
    background: #fff;
    color: #3c4043;
    border: 1px solid #dadce0;
    border-radius: var(--radius-sm, 6px);
    font-family: inherit;
    font-weight: 600;
    font-size: 14px;
    letter-spacing: 0.3px;
    cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s;
  }
  .google-btn:hover:not(:disabled) {
    background: #f8f9fa;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }
  .google-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .g-icon { width: 18px; height: 18px; flex-shrink: 0; }

  .google-finish .muted {
    color: #1976D2;
    font-weight: 600;
    font-size: 12px;
    margin: 0 0 4px;
  }
</style>
